import React, { useState } from 'react';
import { IconButton, ListItemText, Menu, MenuItem } from '@mui/material';
import MoreIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/More.js';

export type FormRowActionMenuAction =
  | 'edit'
  | 'archive'
  | 'duplicate'
  | 'auditLog'
  | 'delete'
  | 'unpublish';

const DESTRUCTIVE_ACTIONS = new Set<FormRowActionMenuAction>(['delete']);

const DEFAULT_MENU_ITEMS: { id: FormRowActionMenuAction; label: string }[] = [
  { id: 'edit', label: 'Edit' },
  { id: 'duplicate', label: 'Duplicate' },
  { id: 'unpublish', label: 'Unpublish' },
  { id: 'delete', label: 'Delete' },
  { id: 'archive', label: 'Archive' },
  { id: 'auditLog', label: 'Audit log' },
];

export interface FormRowActionMenuProps {
  formId: string;
  onEdit?: () => void;
  onArchive?: () => void;
  onDuplicate?: () => void;
  onAuditLog?: () => void;
  onDelete?: () => void;
  onUnpublish?: () => void;
  hiddenActions?: FormRowActionMenuAction[];
  disabledActions?: FormRowActionMenuAction[];
  labelOverrides?: Partial<Record<FormRowActionMenuAction, string>>;
  'data-testid'?: string;
}

export const FormRowActionMenu = ({
  formId: _formId,
  onEdit,
  onArchive,
  onDuplicate,
  onAuditLog,
  onDelete,
  onUnpublish,
  hiddenActions = [],
  disabledActions = [],
  labelOverrides = {},
  'data-testid': dataTestId = 'form-row-action-menu',
}: FormRowActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handlers: Record<FormRowActionMenuAction, (() => void) | undefined> = {
    edit: onEdit,
    archive: onArchive,
    duplicate: onDuplicate,
    auditLog: onAuditLog,
    delete: onDelete,
    unpublish: onUnpublish,
  };

  const menuItems = DEFAULT_MENU_ITEMS.filter(
    (item) =>
      !hiddenActions.includes(item.id) &&
      (handlers[item.id] != null || disabledActions.includes(item.id)),
  ).map((item) => ({
    ...item,
    label: labelOverrides[item.id] ?? item.label,
    disabled: disabledActions.includes(item.id),
  }));

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick =
    (item: (typeof menuItems)[number]) => (e: React.MouseEvent) => {
      e.stopPropagation();
      if (item.disabled) return;
      setAnchorEl(null);
      handlers[item.id]?.();
    };

  return (
    <div data-testid={dataTestId} onClick={(e) => e.stopPropagation()}>
      <IconButton
        onClick={handleOpen}
        aria-label="Form actions"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${dataTestId}-panel` : undefined}
        data-testid={`${dataTestId}-trigger`}
        size="small"
      >
        <MoreIcon aria-hidden />
      </IconButton>
      <Menu
        id={`${dataTestId}-panel`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ list: { 'aria-label': 'Form actions' } }}
        data-testid={`${dataTestId}-panel`}
      >
        {menuItems.map((item, index) => (
          <MenuItem
            key={item.id}
            onClick={handleItemClick(item)}
            disabled={item.disabled}
            color={DESTRUCTIVE_ACTIONS.has(item.id) ? 'destructive' : undefined}
            divider={index < menuItems.length - 1}
            data-testid={`${dataTestId}-item-${item.id}`}
          >
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};
